import React, { useState, useEffect } from 'react';
import {
    MDBModal,
    MDBModalHeader,
    MDBModalBody,
    MDBModalFooter,
    MDBBtn,
    MDBIcon,
    MDBSpinner,
    MDBProgress,
    MDBTable
} from 'mdbreact';
import * as Excel from 'exceljs';
import { saveAs } from 'file-saver';
import moment from 'moment';
import firebase from '../../../firebase';

const MEASURE_TYPE_MAP = {
    field: { label: '現地測定値', color: '#2E75B6', sheetName: 'FieldMeasure' },
    lab: { label: 'ラボ測定値', color: '#B71C1C', sheetName: 'labMeasure' },
    automatic: { label: '自動測定値', color: '#FB8804', sheetName: 'autoMeasure' }
};

const getCellValue = (cell) => {
    if (!cell) return null;
    let val = cell.value;
    if (val === null || val === undefined) return null;
    if (typeof val === 'object') {
        if (val instanceof Date) return val;
        if (val.result !== undefined) return val.result;
        if (val.text !== undefined) return val.text;
        if (Array.isArray(val.richText)) return val.richText.map(t => t.text).join('');
    }
    return val;
};

const parseExcelDate = (val) => {
    if (!val) return null;
    if (val instanceof Date && !isNaN(val.getTime())) {
        return val;
    }
    if (typeof val === 'object' && val.result !== undefined) {
        return parseExcelDate(val.result);
    }
    if (typeof val === 'number') {
        // Excel serial date formula
        const date = new Date(Math.round((val - (25567 + 2)) * 86400 * 1000));
        if (!isNaN(date.getTime())) return date;
    }
    if (typeof val === 'string') {
        const trimmed = val.trim();
        const formats = [
            'YYYY/MM/DD HH:mm',
            'YYYY-MM-DD HH:mm',
            'YYYY/MM/DD HH:mm:ss',
            'YYYY-MM-DD HH:mm:ss',
            'YYYY/M/D H:m',
            'YYYY-M-D H:m',
            'YYYY/MM/DD',
            'YYYY-MM-DD'
        ];
        for (let fmt of formats) {
            const m = moment(trimmed, fmt, true);
            if (m.isValid()) return m.toDate();
        }
        const fallbackMoment = moment(trimmed);
        if (fallbackMoment.isValid()) return fallbackMoment.toDate();
    }
    return null;
};

const isDateColHeader = (str) => {
    if (!str) return false;
    const lower = String(str).trim().toLowerCase();
    return lower.includes('測定日') || lower.includes('日時') || lower.includes('date') || lower.includes('時間') || lower.includes('time');
};

const formatToleranceStr = (item) => {
    const min = item.tolerance_lvl_min;
    const max = item.tolerance_lvl_max;
    const unit = item.unit || '';
    const hasMin = min !== undefined && min !== null && String(min).trim() !== '';
    const hasMax = max !== undefined && max !== null && String(max).trim() !== '';

    if (hasMin && hasMax) {
        return `${min}~${max}${unit}`;
    } else if (hasMin) {
        return `${min}~${unit}`;
    } else if (hasMax) {
        return `~${max}${unit}`;
    }
    return unit;
};

const ExcelImportModal = ({
    isOpen,
    toggle,
    db,
    measurementId,
    processData = [],
    selectedProcessId = '',
    defaultMeasureType = 'all',
    onSuccess
}) => {
    const [targetProcessId, setTargetProcessId] = useState('');
    const [targetMeasureType, setTargetMeasureType] = useState('all');
    const [file, setFile] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [importing, setImporting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [parsedRecords, setParsedRecords] = useState([]);
    const [parsedComments, setParsedComments] = useState([]);
    const [previewSummary, setPreviewSummary] = useState(null);
    const [warnings, setWarnings] = useState([]);
    const [errors, setErrors] = useState([]);

    useEffect(() => {
        if (isOpen) {
            if (selectedProcessId) {
                setTargetProcessId(selectedProcessId);
            } else if (processData.length > 0) {
                setTargetProcessId(processData[0].id);
            }
            setTargetMeasureType(defaultMeasureType);
            resetState();
        }
    }, [isOpen, selectedProcessId, defaultMeasureType, processData]);

    const resetState = () => {
        setFile(null);
        setParsing(false);
        setImporting(false);
        setProgress(0);
        setParsedRecords([]);
        setParsedComments([]);
        setPreviewSummary(null);
        setWarnings([]);
        setErrors([]);
    };

    // Helper to fetch process items directly from Firestore if not present in processData
    const fetchProcessItems = async (procId) => {
        let itemsList = [];
        const proc = processData.find(p => p.id === procId);
        if (proc && proc.items && proc.items.length > 0) {
            itemsList = proc.items;
        } else if (db && measurementId && procId) {
            try {
                const itemsSnap = await db
                    .collection('measurement')
                    .doc(measurementId)
                    .collection('process')
                    .doc(procId)
                    .collection('items')
                    .orderBy('index', 'asc')
                    .get();

                if (itemsSnap.docs.length > 0) {
                    itemsList = itemsSnap.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));
                }
            } catch (err) {
                console.error('Error fetching process items from Firestore:', err);
            }
        }
        return itemsList;
    };

    // Download Excel Template matching Image 1 (Horizontal) or Image 2 (Vertical) formats
    const downloadTemplate = async (templateLayout = 'horizontal') => {
        try {
            const currentProcess = processData.find(p => p.id === targetProcessId) || processData[0];
            if (!currentProcess) {
                alert('工程が選択されていません。');
                return;
            }

            const items = await fetchProcessItems(currentProcess.id);
            if (items.length === 0) {
                alert('選択された工程に管理項目（Items）が見つかりませんでした。');
                return;
            }

            const workbook = new Excel.Workbook();
            const typesToInclude = targetMeasureType === 'all'
                ? ['field', 'lab', 'automatic']
                : [targetMeasureType];

            typesToInclude.forEach((typeKey) => {
                const config = MEASURE_TYPE_MAP[typeKey];
                const worksheet = workbook.addWorksheet(config.sheetName);

                if (templateLayout === 'vertical') {
                    // IMAGE 2 FORMAT (Vertical Layout):
                    // Col A: 管理項目, Col B: 許容範囲, Col C: 測定日, Col D: 価値, Col E: コメント
                    const headerRow = worksheet.getRow(1);
                    headerRow.height = 25;
                    headerRow.getCell(1).value = '管理項目';
                    headerRow.getCell(2).value = '許容範囲';
                    headerRow.getCell(3).value = '測定日';
                    headerRow.getCell(4).value = '価値';
                    headerRow.getCell(5).value = 'コメント';

                    [1, 2, 3, 4, 5].forEach(colIdx => {
                        const cell = headerRow.getCell(colIdx);
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
                        cell.font = { bold: true };
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    });

                    worksheet.getColumn(1).width = 30;
                    worksheet.getColumn(2).width = 20;
                    worksheet.getColumn(3).width = 20;
                    worksheet.getColumn(4).width = 15;
                    worksheet.getColumn(5).width = 25;

                    // Add registered process items (each item listed once)
                    items.forEach((item, idx) => {
                        const row = worksheet.getRow(2 + idx);
                        row.getCell(1).value = item.item_name || '';
                        row.getCell(2).value = formatToleranceStr(item);
                        row.getCell(3).value = moment().format('YYYY/MM/DD 09:00');
                        row.getCell(4).value = '';
                        row.getCell(5).value = '';
                    });
                } else {
                    // IMAGE 1 FORMAT (Horizontal Layout):
                    // Row 1: Col A: 管理項目, Col B: 許容範囲, Col C: 2026/08/26 09:00, Col D: 2026/08/26 10:00
                    const headerRow = worksheet.getRow(1);
                    headerRow.height = 25;
                    headerRow.getCell(1).value = '管理項目';
                    headerRow.getCell(2).value = '許容範囲';
                    headerRow.getCell(3).value = moment().format('YYYY/MM/DD 09:00');
                    headerRow.getCell(4).value = moment().format('YYYY/MM/DD 10:00');

                    [1, 2, 3, 4].forEach(colIdx => {
                        const cell = headerRow.getCell(colIdx);
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
                        cell.font = { bold: true };
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    });

                    worksheet.getColumn(1).width = 30;
                    worksheet.getColumn(2).width = 20;
                    worksheet.getColumn(3).width = 20;
                    worksheet.getColumn(4).width = 20;

                    // Add registered process items and tolerance values to rows
                    items.forEach((item, idx) => {
                        const row = worksheet.getRow(2 + idx);
                        row.getCell(1).value = item.item_name || '';
                        row.getCell(2).value = formatToleranceStr(item);
                        row.getCell(3).value = '';
                        row.getCell(4).value = '';
                    });
                }
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const procName = currentProcess.process_name || 'Process';
            saveAs(blob, `${procName}_Measurement_Template_${templateLayout}.xlsx`);
        } catch (err) {
            console.error('Download template error:', err);
            alert('テンプレート作成中にエラーが発生しました: ' + err.message);
        }
    };

    const handleFileSelect = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;
        setFile(selectedFile);
        parseExcelFile(selectedFile);
    };

    const parseExcelFile = async (targetFile) => {
        setParsing(true);
        setWarnings([]);
        setErrors([]);
        setParsedRecords([]);
        setParsedComments([]);
        setPreviewSummary(null);

        const currentProcess = processData.find(p => p.id === targetProcessId);
        if (!currentProcess) {
            setErrors(['対象の工程を選択してください。']);
            setParsing(false);
            return;
        }

        const itemsList = await fetchProcessItems(targetProcessId);
        if (itemsList.length === 0) {
            setErrors(['選択された工程に管理項目（Items）が存在しません。']);
            setParsing(false);
            return;
        }

        const itemLookup = {};
        itemsList.forEach(item => {
            if (item.item_name) {
                itemLookup[item.item_name.trim().toLowerCase()] = item;
            }
        });

        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const buffer = evt.target.result;
                const workbook = new Excel.Workbook();
                await workbook.xlsx.load(buffer);

                const extractedRecords = [];
                const extractedComments = [];
                const warningList = [];
                const errorList = [];
                const matchedItemSet = new Set();
                const timestampsSet = new Set();
                let detectedLayouts = new Set();

                let earliestDate = null;
                let latestDate = null;

                workbook.worksheets.forEach((worksheet) => {
                    const sheetName = worksheet.name.trim().toLowerCase();
                    let sheetMeasureType = targetMeasureType;

                    if (targetMeasureType === 'all') {
                        if (sheetName.includes('field') || sheetName.includes('現地')) {
                            sheetMeasureType = 'field';
                        } else if (sheetName.includes('lab') || sheetName.includes('ラボ')) {
                            sheetMeasureType = 'lab';
                        } else if (sheetName.includes('auto') || sheetName.includes('自動')) {
                            sheetMeasureType = 'automatic';
                        } else {
                            sheetMeasureType = 'field';
                        }
                    }

                    if (worksheet.rowCount < 2) return;

                    const row1 = worksheet.getRow(1);
                    const cellC1 = getCellValue(row1.getCell(3));

                    // Check layout orientation based on Row 1 (Image 1 vs Image 2)
                    const isVerticalFormat = isDateColHeader(cellC1);

                    if (isVerticalFormat) {
                        // ----------------------------------------------------
                        // IMAGE 2 FORMAT (Vertical Layout):
                        // Col A: 管理項目, Col B: 許容範囲, Col C: 測定日, Col D: 価値, Col E: コメント
                        // ----------------------------------------------------
                        detectedLayouts.add('縦方向 (Image 2 Format)');

                        worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
                            if (rowNumber === 1) return;

                            const rawItemName = getCellValue(row.getCell(1));
                            if (!rawItemName) return;

                            const cleanItemName = String(rawItemName).trim();
                            const matchedItem = itemLookup[cleanItemName.toLowerCase()];
                            if (!matchedItem) {
                                warningList.push(`シート 「${worksheet.name}」 行 ${rowNumber}: 管理項目 「${cleanItemName}」 は一致しません。`);
                                return;
                            }

                            const dateVal = parseExcelDate(getCellValue(row.getCell(3)));
                            if (!dateVal) {
                                warningList.push(`シート 「${worksheet.name}」 行 ${rowNumber}: 測定日が正しい日付フォーマットではありません。`);
                                return;
                            }

                            matchedItemSet.add(matchedItem.id);
                            timestampsSet.add(dateVal.getTime());
                            if (!earliestDate || dateVal < earliestDate) earliestDate = dateVal;
                            if (!latestDate || dateVal > latestDate) latestDate = dateVal;

                            const valCell = getCellValue(row.getCell(4));
                            if (valCell !== null && valCell !== undefined && valCell !== '') {
                                const numVal = parseFloat(valCell);
                                const finalVal = !isNaN(numVal) ? String(numVal) : String(valCell).trim();
                                extractedRecords.push({
                                    processId: targetProcessId,
                                    itemId: matchedItem.id,
                                    itemName: matchedItem.item_name,
                                    measureType: sheetMeasureType,
                                    insertedDate: dateVal,
                                    value: finalVal
                                });
                            }

                            const commentCell = getCellValue(row.getCell(5));
                            if (commentCell !== null && commentCell !== undefined && String(commentCell).trim() !== '') {
                                extractedComments.push({
                                    processId: targetProcessId,
                                    measureType: sheetMeasureType,
                                    insertedDate: dateVal,
                                    comment: String(commentCell).trim()
                                });
                            }
                        });
                    } else {
                        // ----------------------------------------------------
                        // IMAGE 1 FORMAT (Horizontal Layout):
                        // Col A: 管理項目, Col B: 許容範囲, Col C+: Date/Time headers
                        // ----------------------------------------------------
                        detectedLayouts.add('横方向 (Image 1 Format)');

                        let startCol = 3;
                        const col2Header = getCellValue(row1.getCell(2));
                        if (col2Header && parseExcelDate(col2Header)) {
                            startCol = 2;
                        }

                        const colDateMap = {};
                        row1.eachCell({ includeEmpty: false }, (cell, colNumber) => {
                            if (colNumber >= startCol) {
                                const rawVal = getCellValue(cell);
                                const parsedDate = parseExcelDate(rawVal);
                                if (parsedDate) {
                                    colDateMap[colNumber] = parsedDate;
                                    timestampsSet.add(parsedDate.getTime());

                                    if (!earliestDate || parsedDate < earliestDate) earliestDate = parsedDate;
                                    if (!latestDate || parsedDate > latestDate) latestDate = parsedDate;
                                }
                            }
                        });

                        if (Object.keys(colDateMap).length === 0) {
                            warningList.push(`シート 「${worksheet.name}」 に日付ヘッダーが見つかりませんでした。`);
                            return;
                        }

                        worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
                            if (rowNumber === 1) return;

                            const rawItemName = getCellValue(row.getCell(1));
                            if (!rawItemName) return;

                            const cleanItemName = String(rawItemName).trim();
                            const matchedItem = itemLookup[cleanItemName.toLowerCase()];

                            if (!matchedItem) {
                                warningList.push(`シート 「${worksheet.name}」: 管理項目 「${cleanItemName}」 は対象工程の項目と一致しませんでした。`);
                                return;
                            }

                            matchedItemSet.add(matchedItem.id);

                            Object.keys(colDateMap).forEach((colIdxStr) => {
                                const colIdx = Number(colIdxStr);
                                const dateObj = colDateMap[colIdx];
                                const cellVal = getCellValue(row.getCell(colIdx));

                                if (cellVal !== null && cellVal !== undefined && cellVal !== '') {
                                    const numVal = parseFloat(cellVal);
                                    const finalValue = !isNaN(numVal) ? String(numVal) : String(cellVal).trim();

                                    extractedRecords.push({
                                        processId: targetProcessId,
                                        itemId: matchedItem.id,
                                        itemName: matchedItem.item_name,
                                        measureType: sheetMeasureType,
                                        insertedDate: dateObj,
                                        value: finalValue
                                    });
                                }
                            });
                        });
                    }
                });

                if (extractedRecords.length === 0 && extractedComments.length === 0) {
                    errorList.push('インポート可能な測定データまたはコメントが見つかりませんでした。ファイル内容をご確認ください。');
                }

                setParsedRecords(extractedRecords);
                setParsedComments(extractedComments);
                setWarnings(warningList);
                setErrors(errorList);
                setPreviewSummary({
                    processName: currentProcess.process_name,
                    detectedLayout: Array.from(detectedLayouts).join(', ') || '不明',
                    totalRecords: extractedRecords.length,
                    totalComments: extractedComments.length,
                    matchedItemsCount: matchedItemSet.size,
                    totalProcessItems: itemsList.length,
                    uniqueTimestampsCount: timestampsSet.size,
                    earliestDate: earliestDate ? moment(earliestDate).format('YYYY/MM/DD HH:mm') : '-',
                    latestDate: latestDate ? moment(latestDate).format('YYYY/MM/DD HH:mm') : '-'
                });
            } catch (err) {
                console.error('Excel parse error:', err);
                setErrors(['Excelファイルの解析中にエラーが発生しました: ' + err.message]);
            } finally {
                setParsing(false);
            }
        };

        reader.readAsArrayBuffer(targetFile);
    };

    // Execute High-Performance Batch Import (Measurements + Comments)
    const handleExecuteImport = async () => {
        if (parsedRecords.length === 0 && parsedComments.length === 0) return;
        setImporting(true);
        setProgress(0);

        try {
            const chunkSize = 400;
            const allWriteOperations = [];

            // 1. Measurement record operations
            parsedRecords.forEach((rec) => {
                const docId = `${rec.itemId}_${rec.insertedDate.getTime()}`;
                const docRef = db
                    .collection('measurement')
                    .doc(measurementId)
                    .collection('process')
                    .doc(rec.processId)
                    .collection('items')
                    .doc(rec.itemId)
                    .collection(`${rec.measureType}Measure`)
                    .doc(docId);

                allWriteOperations.push({
                    ref: docRef,
                    data: {
                        value: rec.value,
                        insertedDate: firebase.firestore.Timestamp.fromDate(rec.insertedDate),
                        edit: false
                    }
                });
            });

            // 2. Comment record operations
            parsedComments.forEach((cmnt) => {
                const commentDocId = `${cmnt.insertedDate.getTime()}`;
                const commentRef = db
                    .collection('measurement')
                    .doc(measurementId)
                    .collection('process')
                    .doc(cmnt.processId)
                    .collection(`${cmnt.measureType}Comments`)
                    .doc(commentDocId);

                allWriteOperations.push({
                    ref: commentRef,
                    data: {
                        comment: cmnt.comment,
                        insertedDate: firebase.firestore.Timestamp.fromDate(cmnt.insertedDate),
                        edit: false
                    }
                });
            });

            const total = allWriteOperations.length;
            let processed = 0;

            for (let i = 0; i < total; i += chunkSize) {
                const chunk = allWriteOperations.slice(i, i + chunkSize);
                const batch = db.batch();

                chunk.forEach((op) => {
                    batch.set(op.ref, op.data, { merge: true });
                });

                await batch.commit();
                processed += chunk.length;
                setProgress(Math.round((processed / total) * 100));

                await new Promise(resolve => setTimeout(resolve, 10));
            }

            setImporting(false);
            if (onSuccess) onSuccess();
            toggle();
        } catch (err) {
            console.error('Import error:', err);
            setErrors(['インポート中にエラーが発生しました: ' + err.message]);
            setImporting(false);
        }
    };

    return (
        <MDBModal isOpen={isOpen} toggle={toggle} size="lg" centered>
            <MDBModalHeader toggle={toggle} className="bg-primary text-white">
                <MDBIcon icon="file-excel" className="mr-2" /> Excelデータインポート
            </MDBModalHeader>
            <MDBModalBody>
                <div className="mb-3">
                    <label className="font-weight-bold">対象工程：</label>
                    <select
                        className="browser-default custom-select mb-2"
                        value={targetProcessId}
                        onChange={(e) => {
                            setTargetProcessId(e.target.value);
                            resetState();
                        }}
                        disabled={parsing || importing}
                    >
                        {processData.map((proc) => (
                            <option key={proc.id} value={proc.id}>
                                {proc.process_name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="mb-3">
                    <label className="font-weight-bold">測定タイプ：</label>
                    <select
                        className="browser-default custom-select mb-2"
                        value={targetMeasureType}
                        onChange={(e) => {
                            setTargetMeasureType(e.target.value);
                            if (file) parseExcelFile(file);
                        }}
                        disabled={parsing || importing}
                    >
                        <option value="all">すべて (シート名自動判定)</option>
                        <option value="field">現地測定値 (Field)</option>
                        <option value="lab">ラボ測定値 (Lab)</option>
                        <option value="automatic">自動測定値 (Auto)</option>
                    </select>
                </div>

                <div className="d-flex align-items-center mb-3">
                    <span className="mr-2 font-weight-bold" style={{ fontSize: 13 }}>テンプレートダウンロード:</span>
                    <MDBBtn color="secondary" size="sm" onClick={() => downloadTemplate('horizontal')} disabled={parsing || importing} className="mr-2">
                        <MDBIcon icon="download" className="mr-1" /> 横方向 (画像1形式)
                    </MDBBtn>
                    <MDBBtn color="info" size="sm" onClick={() => downloadTemplate('vertical')} disabled={parsing || importing}>
                        <MDBIcon icon="download" className="mr-1" /> 縦方向 (画像2形式)
                    </MDBBtn>
                </div>

                <div className="custom-file mb-3">
                    <input
                        type="file"
                        className="custom-file-input"
                        id="excelFileInput"
                        accept=".xlsx, .xls"
                        onChange={handleFileSelect}
                        disabled={parsing || importing}
                    />
                    <label className="custom-file-label" htmlFor="excelFileInput">
                        {file ? file.name : 'Excelファイルを選択してください...'}
                    </label>
                </div>

                {parsing && (
                    <div className="text-center my-3">
                        <MDBSpinner small color="primary" /> 解析中...
                    </div>
                )}

                {errors.length > 0 && (
                    <div className="alert alert-danger">
                        {errors.map((err, idx) => (
                            <div key={idx}><MDBIcon icon="exclamation-circle" className="mr-1" /> {err}</div>
                        ))}
                    </div>
                )}

                {warnings.length > 0 && (
                    <div className="alert alert-warning style-scroll" style={{ maxHeight: 120, overflowY: 'auto' }}>
                        <div className="font-weight-bold mb-1"><MDBIcon icon="exclamation-triangle" className="mr-1" /> 警告 ({warnings.length} 件):</div>
                        {warnings.map((warn, idx) => (
                            <div key={idx} style={{ fontSize: 13 }}>• {warn}</div>
                        ))}
                    </div>
                )}

                {previewSummary && (
                    <div className="card p-3 my-3 bg-light">
                        <h6 className="font-weight-bold text-primary mb-2">
                            <MDBIcon icon="check-circle" className="mr-1" /> インポートプレビュー情報
                        </h6>
                        <MDBTable small borderless responsive className="mb-0">
                            <tbody>
                                <tr>
                                    <td><strong>工程名:</strong> {previewSummary.processName}</td>
                                    <td><strong>検出レイアウト:</strong> {previewSummary.detectedLayout}</td>
                                </tr>
                                <tr>
                                    <td><strong>測定データ数:</strong> {previewSummary.totalRecords.toLocaleString()} 件</td>
                                    <td><strong>コメント数:</strong> {previewSummary.totalComments.toLocaleString()} 件</td>
                                </tr>
                                <tr>
                                    <td><strong>一致した管理項目:</strong> {previewSummary.matchedItemsCount} / {previewSummary.totalProcessItems} 項目</td>
                                    <td><strong>測定日時数:</strong> {previewSummary.uniqueTimestampsCount.toLocaleString()} 時刻</td>
                                </tr>
                                <tr>
                                    <td colSpan={2}><strong>期間:</strong> {previewSummary.earliestDate} 〜 {previewSummary.latestDate}</td>
                                </tr>
                            </tbody>
                        </MDBTable>
                    </div>
                )}

                {importing && (
                    <div className="my-3 text-center">
                        <div className="mb-1 font-weight-bold">Firestoreへ一括保存中... {progress}%</div>
                        <MDBProgress value={progress} color="success" animated />
                    </div>
                )}
            </MDBModalBody>
            <MDBModalFooter>
                <MDBBtn color="grey" onClick={toggle} disabled={importing}>
                    キャンセル
                </MDBBtn>
                <MDBBtn
                    color="primary"
                    onClick={handleExecuteImport}
                    disabled={(!parsedRecords.length && !parsedComments.length) || parsing || importing || errors.length > 0}
                >
                    {importing ? <MDBSpinner small /> : <><MDBIcon icon="upload" className="mr-1" /> インポート実行</>}
                </MDBBtn>
            </MDBModalFooter>
        </MDBModal>
    );
};

export default ExcelImportModal;
